import { useEffect, useMemo, useRef, useState } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { Button, themeColors } from '@hhgtech/hhg-components/mantine'
import { Close } from '@hhgtech/icons/core'
import { ShareThin } from '@hhgtech/icons/other'
import { AspectRatio, Box, BoxProps, Transition } from '@mantine/core'
import { useResizeObserver } from '@mantine/hooks'
import { useIntl } from 'react-intl'

import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'

import useStyles from './index.styles'
import { useSocialListRevamp } from '../asideNav'
import { CopyInput, CopyInputProps } from '../copyInput'
import { SocialItemEle } from '../socialItemEle'
import { SocialItem } from '../types'
import { getSocialListByLocale } from '../utils'

type Props = {
  shareUrl: string
  numberToExpand?: number
  iconSize?: number
  showFull?: boolean
  padding?: number
  touchSize?: number
  hasOverlay?: boolean
  isHeader?: boolean
  copyInputProps?: CopyInputProps
  bgColor?: string
  bgColorCopyBtn?: string
  isRevamp?: boolean
} & BoxProps

export const BottomNavHorizonal = ({
  styles,
  classNames,
  numberToExpand = 3,
  shareUrl,
  showFull: showFullProp,
  iconSize = 28,
  padding = 4,
  touchSize = 36,
  hasOverlay,
  isHeader,
  copyInputProps,
  bgColor = themeColors.gray[1],
  bgColorCopyBtn = theme.colors.white,
  isRevamp,
  ...rest
}: Props) => {
  const { formatMessage: f } = useIntl()
  const currentUrl = useCurrentUrl()
  const { locale, asPath } = useRouterExtend()
  const isMobile = useIsMobile()
  const socialListProps = useMemo(
    () => getSocialListByLocale(locale, shareUrl, iconSize, isMobile),
    [shareUrl, isMobile],
  )
  const showFull = useMemo(
    () => showFullProp || socialListProps.length < numberToExpand,
    [socialListProps, asPath],
  )
  const [socialList, setSocialList] = useState<SocialItem[]>([])
  const [startCalc, setStartCalc] = useState(false)
  const [navigatorCanShare, setNavigatorCanShare] = useState(false)

  const [ref, rect] = useResizeObserver()
  const [opened, setOpened] = useState<null | boolean>(null)
  const lastTextWidth = useRef(0)

  const { classes, cx } = useStyles(
    {
      showFull,
      startCalc,
      textWidth: lastTextWidth.current + padding,
      opened,
      length: socialList.length,
      numberToExpand,
      iconSize,
      padding,
      touchSize,
      bgColor,
      bgColorCopyBtn,
    },
    {
      name: 'ShareBox--Horizonal',
      styles,
      classNames,
    },
  )

  const copyBtn = useMemo(
    () => (
      <CopyInput
        isHeader={isHeader}
        shareUrl={shareUrl}
        type="button"
        className="copy-btn"
        classNames={{
          root: classes.copyRoot,
          heading: classes.copyHeading,
          inputWrapper: classes.copyInputWrapper,
          inputRightSection: classes.copyInputRightSection,
          input: classes.copyInput,
          btn: classes.copyBtn,
        }}
        popoverProps={{
          withinPortal: true,
          position: 'bottom',
        }}
        {...copyInputProps}
      />
    ),
    [shareUrl],
  )

  useEffect(() => {
    if (rect.width > 0 && !startCalc) {
      setStartCalc(true)
      lastTextWidth.current = rect.width
    }
    if (startCalc) setSocialList(socialListProps)
  }, [rect.width, startCalc])

  useEffect(() => {
    setStartCalc(false)
    lastTextWidth.current = 0
  }, [isMobile])

  const shareData = useMemo(() => {
    const title = typeof document !== 'undefined' ? document.title : ''
    return {
      url: shareUrl,
      title,
      text: title,
    }
  }, [shareUrl])

  useEffect(() => {
    setNavigatorCanShare(navigator?.canShare?.(shareData))
  }, [shareUrl])

  const socialListRevamp = useSocialListRevamp(socialList, isRevamp)

  if (isRevamp && navigatorCanShare) {
    return (
      <Box
        className="w-10 h-10 p-2 relative cursor-pointer"
        data-event-category="Sticky Botton"
        data-event-action="Social Share - article"
        data-event-label="Click to share"
        onClick={() => {
          navigator?.share(shareData).catch((error) => {
            console.error(error)
          })
        }}
      >
        <ShareThin />
      </Box>
    )
  }

  return (
    <>
      <Box className={classes.root} {...rest}>
        <Box className={classes.frame} />
        <Box className={classes.wrapper}>
          <Box className={classes.inner}>
            <div ref={ref}>
              <div className={classes.heading}>
                {f({
                  id: 'shareArticle.asideNav.share',
                  defaultMessage: 'Share',
                })}
              </div>
            </div>
            <div className={classes.listItems}>
              {socialListRevamp.map((social: SocialItem, index: number) => (
                <SocialItemEle
                  key={index}
                  social={social}
                  iconSize={iconSize}
                  bgZaloColor={bgColor}
                  dataEventAction={
                    isHeader ? `Header - ${social.name}` : social.name
                  }
                />
              ))}
              {opened !== null && (
                <AspectRatio ratio={1} className={classes.item}>
                  <Box className={cx(classes.itemInner)}>{copyBtn}</Box>
                </AspectRatio>
              )}
            </div>
          </Box>
          <div className={cx(classes.controls)}>
            <AspectRatio ratio={1} className={classes.item}>
              <Box className={classes.itemInner}>
                {opened ? (
                  <>
                    <Button
                      radius={'100%'}
                      data-event-category="Social Share"
                      data-event-action="See More"
                      data-event-label={currentUrl}
                      className={cx(
                        'pointer-event-child-none',
                        classes.controlBtn,
                      )}
                      onClick={() => setOpened(false)}
                      leftIcon={<Close size={20} />}
                      styles={() => ({
                        root: {
                          padding: 0,
                          width: `${iconSize}px !important`,
                          height: `${iconSize}px !important`,
                          backgroundColor: `#fff !important`,
                        },
                        label: {
                          display: 'none',
                        },
                        leftIcon: {
                          marginRight: 0,
                        },
                      })}
                    />
                  </>
                ) : showFull ? (
                  copyBtn
                ) : (
                  <Button
                    radius={'100%'}
                    data-event-category="Social Share"
                    data-event-action="See More"
                    data-event-label={currentUrl}
                    className={cx(
                      'pointer-event-child-none',
                      classes.controlBtn,
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpened(true)
                    }}
                    leftIcon={
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.67379 10.125C4.36443 10.125 4.09959 10.0148 3.87928 9.79449C3.65898 9.57418 3.54883 9.30934 3.54883 8.99998C3.54883 8.69062 3.65898 8.42578 3.87928 8.20547C4.09959 7.98516 4.36443 7.875 4.67379 7.875C4.98317 7.875 5.248 7.98516 5.4683 8.20547C5.68862 8.42578 5.79877 8.69062 5.79877 8.99998C5.79877 9.30934 5.68862 9.57418 5.4683 9.79449C5.248 10.0148 4.98317 10.125 4.67379 10.125ZM9.00071 10.125C8.69135 10.125 8.42651 10.0148 8.2062 9.79449C7.98588 9.57418 7.87573 9.30934 7.87573 8.99998C7.87573 8.69062 7.98588 8.42578 8.2062 8.20547C8.42651 7.98516 8.69135 7.875 9.00071 7.875C9.31007 7.875 9.57491 7.98516 9.79522 8.20547C10.0155 8.42578 10.1257 8.69062 10.1257 8.99998C10.1257 9.30934 10.0155 9.57418 9.79522 9.79449C9.57491 10.0148 9.31007 10.125 9.00071 10.125ZM13.3276 10.125C13.0183 10.125 12.7534 10.0148 12.5331 9.79449C12.3128 9.57418 12.2026 9.30934 12.2026 8.99998C12.2026 8.69062 12.3128 8.42578 12.5331 8.20547C12.7534 7.98516 13.0183 7.875 13.3276 7.875C13.637 7.875 13.9018 7.98516 14.1221 8.20547C14.3424 8.42578 14.4526 8.69062 14.4526 8.99998C14.4526 9.30934 14.3424 9.57418 14.1221 9.79449C13.9018 10.0148 13.637 10.125 13.3276 10.125Z"
                          fill="#595959"
                        />
                      </svg>
                    }
                    styles={() => ({
                      root: {
                        padding: 0,
                        width: `${iconSize}px !important`,
                        height: `${iconSize}px !important`,
                        backgroundColor: `#fff !important`,
                      },
                      label: {
                        display: 'none',
                      },
                      leftIcon: {
                        marginRight: 0,
                      },
                    })}
                  >
                    ...
                  </Button>
                )}
              </Box>
            </AspectRatio>
          </div>
        </Box>
      </Box>
      {hasOverlay && (
        <Transition mounted={Boolean(opened)} transition="fade" duration={300}>
          {(styles) => <div className={classes.overlay} style={styles}></div>}
        </Transition>
      )}
    </>
  )
}
